// components/Sidebar.jsx
import { COLORS, WALLPAPERS as DEMO_WALLPAPERS, TILES as DEMO_TILES } from "../data/materials";
import { DEFAULT_REGION_SETTINGS } from "../data/regionSettings";
import { useEffect, useMemo, useState } from "react";
import { getPublicVisualizerProductFilters, searchPublicVisualizerProducts, toAssetUrl } from "../services/api";
import ProductDetailModal from "./retailer/ProductDetailModal";

function isSameMaterial(a, b) {
  if (!a || !b) return false;
  return a.type === b.type && a.value === b.value;
}

function getProductImage(item) {
  if (item.image) return item.image;
  if (item.image_url) return toAssetUrl(item.image_url);
  if (Array.isArray(item.images) && item.images.length > 0) {
    return toAssetUrl(item.images[0]?.url);
  }
  return null;
}

function dimensionToMm(value, unit) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;

  switch (String(unit || "").toLowerCase()) {
    case "mm":
      return number;
    case "cm":
      return number * 10;
    case "inch":
    case "in":
      return number * 25.4;
    case "feet":
    case "ft":
      return number * 304.8;
    default:
      return null;
  }
}

function normalizeProduct(item, fallbackType) {
  const sizeUnit = item.size?.unit || item.size_unit || null;
  const widthMm = item.width_mm ?? dimensionToMm(item.size?.width, sizeUnit);
  const heightMm = item.height_mm ?? dimensionToMm(item.size?.height, sizeUnit);
  const width = item.width_cm ?? (widthMm != null ? widthMm / 10 : null);
  const height = item.height_cm ?? (heightMm != null ? heightMm / 10 : null);
  return {
    ...item,
    id: item.id,
    name: item.name || item.sku || "Product",
    sku: item.sku,
    type: fallbackType || item.category || "tile",
    image: getProductImage(item),
    size: item.size || null,
    size_unit: sizeUnit,
    width_mm: widthMm,
    height_mm: heightMm,
    width_cm: width,
    height_cm: height,
  };
}

function shortName(name) {
  if (!name) return "Product";
  return name.length > 24 ? `${name.slice(0, 24)}...` : name;
}

function formatSize(item) {
  if (item.size?.width && item.size?.height && item.size?.unit) {
    return `${item.size.width}x${item.size.height} ${item.size.unit}`;
  }
  if (item.product_size) return item.product_size;
  if (item.width_mm && item.height_mm) return `${item.width_mm}x${item.height_mm} mm`;
  return "Size not specified";
}

function ProductBadge({ index }) {
  const labels = ["Best Seller", "On Sale", "Out Of Stock"];
  return (
    <span className="inline-flex w-fit rounded bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-white">
      {labels[index % labels.length]}
    </span>
  );
}

export default function Sidebar({
  onSelect,
  settings,
  setSettings,
  onApply,
  selectedRegion,
  selectedMaterial,
  regionMaterials,
  tiles: tilesProp,
  wallpapers: wallpapersProp,
  brandLogoUrl,
  brandName,
  visualizerToken,
  onClearMaterials,
  onDirectApply,
  onGroutChange,
  onRemoveGrout,
}) {
  const [activeTab, setActiveTab] = useState("tiles");
  const [surfaceFilter, setSurfaceFilter] = useState("wall");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showGroutSettings, setShowGroutSettings] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedMaterialFilter, setSelectedMaterialFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [productLayout, setProductLayout] = useState("list");
  const [availableFilters, setAvailableFilters] = useState(null);
  const [searchedProducts, setSearchedProducts] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);

  const TILES = tilesProp !== undefined ? tilesProp : DEMO_TILES;
  const WALLPAPERS = wallpapersProp !== undefined ? wallpapersProp : DEMO_WALLPAPERS;
  const activeCategory = activeTab === "wallpapers" ? "wallpaper" : "tile";
  const catalogOnly = tilesProp !== undefined || wallpapersProp !== undefined;

  const handleAddToCart = (product) => {
  // Add to cart logic here
};

  useEffect(() => {
    if (!visualizerToken) return;

    let cancelled = false;
    getPublicVisualizerProductFilters(visualizerToken)
      .then((response) => {
        if (!cancelled) setAvailableFilters(response.filters || null);
      })
      .catch((err) => console.error("Failed to load product filters:", err));

    return () => {
      cancelled = true;
    };
  }, [visualizerToken]);

  useEffect(() => {
    if (!visualizerToken || activeTab === "colors") {
      setSearchedProducts(null);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await searchPublicVisualizerProducts(visualizerToken, {
          q: searchTerm,
          category: activeCategory,
          surface: surfaceFilter,
          size: selectedSize,
          material: selectedMaterialFilter,
          sort: sortOrder,
        });
        if (!cancelled) {
          setSearchedProducts((response.products || []).map((item) => normalizeProduct(item, activeCategory)));
        }
      } catch (err) {
        console.error("Failed to search products:", err);
        if (!cancelled) setSearchedProducts(null);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    activeCategory,
    activeTab,
    searchTerm,
    selectedMaterialFilter,
    selectedSize,
    sortOrder,
    surfaceFilter,
    visualizerToken,
  ]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (onGroutChange) {
      onGroutChange(key, value);
    }
  };

  const appliedCount = Object.values(regionMaterials || {}).filter(Boolean).length;
  const selectedRegionName =
    typeof selectedRegion === "string"
      ? selectedRegion.replaceAll("_", " ")
      : "";
  const localProducts = useMemo(
    () => (activeTab === "wallpapers" ? WALLPAPERS : TILES).map((item) => normalizeProduct(item, activeCategory)),
    [activeCategory, activeTab, TILES, WALLPAPERS],
  );
  const visibleProducts = searchedProducts ?? localProducts;
  const productTabs = [];

  const toMaterial = (item) => ({
    ...item,
    id: item.id,
    name: item.name,
    type: activeCategory,
    value: item.image,
    product_size: formatSize(item),
    size: item.size || null,
    size_unit: item.size_unit || item.size?.unit || null,
    width_mm: item.width_mm,
    height_mm: item.height_mm,
    width_cm: item.width_cm,
    height_cm: item.height_cm,
  });

  const renderProductListCard = (item, index) => {
    const material = toMaterial(item);
    const isSelected = isSameMaterial(selectedMaterial, material);

    return (
      <div
        key={item.id}
        onClick={() => {
          onSelect(material);
          if (onDirectApply) {
            onDirectApply(material);
          }
        }}
        className={`w-full cursor-pointer rounded-lg border bg-white p-2 text-left transition ${
          isSelected ? "border-gray-900 ring-1 ring-gray-900" : "border-gray-200 hover:border-gray-400"
        }`}
      >
        <div className="flex gap-3">
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-md border border-gray-100 bg-gray-50">
            {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}
            <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/30 text-white">
              ♥
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold uppercase leading-4 text-gray-900">
              {shortName(item.name)}
            </div>
            <div className="mt-1 text-xs text-gray-600">SKU: {item.sku || "-"}</div>
            <div className="text-xs text-gray-600">Size: {formatSize(item)}</div>
            <div className="text-xs text-gray-600">{item.surface_type || item.application_type || "-"}</div>
            <div className="mt-1">
              <ProductBadge index={index} />
            </div>
          </div>
        </div>

        {/* Trigger Modal without selecting card */}
        <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setDetailProduct(item);
      }}
      className="mt-2 w-full border-t border-gray-100 pt-2 text-center text-xs text-gray-600 hover:text-black"
    >
      More Product Detail
      <span className="ml-2 text-base leading-none">→</span>
    </button>

    <ProductDetailModal
      product={detailProduct}
      onClose={() => setDetailProduct(null)}
      onAddToCart={handleAddToCart}
      formatSize={formatSize}
    />
      </div>

      
    );
  };

  const renderProductGridCard = (item) => {
    const material = toMaterial(item);
    const isSelected = isSameMaterial(selectedMaterial, material);

    return (
      <button
        key={item.id}
        type="button"
        onClick={() => {
          onSelect(material);
          if (onDirectApply) {
            onDirectApply(material);
          }
        }}
        className={`relative aspect-square overflow-hidden rounded-lg border bg-gray-50 transition ${
          isSelected ? "border-gray-900 ring-1 ring-gray-900" : "border-gray-200 hover:border-gray-400"
        }`}
      >
        {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}
        <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/30 text-white">
          ♥
        </span>
      </button>
    );
  };

  const renderContent = () => {
    if (activeTab === "colors") {
      return (
        <div className="grid grid-cols-3 gap-3">
          {COLORS.map((color) => {
            const material = { type: "color", value: color };
            const isSelected = isSameMaterial(selectedMaterial, material);

            return (
              <button
                key={color}
                type="button"
                onClick={() => {
                  onSelect(material);
                  if (onDirectApply) {
                    onDirectApply(material);
                  }
                }}
                className={`group relative h-16 rounded-lg border transition ${
                  isSelected ? "border-gray-900 ring-2 ring-gray-900" : "border-gray-200 hover:border-gray-500"
                }`}
                style={{ backgroundColor: color }}
                title={color}
              >
                <span className="sr-only">{color}</span>
                {isSelected && (
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white drop-shadow">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    }

    if (searchLoading) {
      return <div className="py-8 text-center text-sm text-gray-500">Loading products...</div>;
    }

    if (visibleProducts.length === 0) {
      return (
        <div className="text-center py-8 px-2">
          <p className="text-sm font-medium text-gray-700">No products found</p>
          <p className="text-xs text-gray-500 mt-2">
            {catalogOnly
              ? "Try changing the search or filters."
              : "No products available yet."}
          </p>
        </div>
      );
    }

    if (productLayout === "grid") {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {visibleProducts.map(renderProductGridCard)}
          </div>
        </div>
      );
    }

    return <div className="space-y-2">{visibleProducts.map(renderProductListCard)}</div>;
  };

  return (
    <>
      <div className="w-80 min-w-[20rem] max-w-[20rem] flex-shrink-0 h-full bg-white border-r border-gray-200 flex flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-3 pb-4">
          {(brandLogoUrl || brandName) && (
            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-200">
              {brandLogoUrl && (
                <img
                  src={brandLogoUrl}
                  alt={brandName || "Company logo"}
                  className="w-10 h-10 rounded-lg object-contain border border-gray-200 bg-white"
                />
              )}
              {brandName && (
                <span className="text-gray-900 font-semibold text-sm truncate">
                  {brandName}
                </span>
              )}
            </div>
          )}

          <p className="text-gray-600 text-sm mb-3">
            {selectedRegion
              ? `Editing: ${selectedRegionName}`
              : "Select a region in the room to apply materials"}
            {appliedCount > 0 && (
              <span className="block mt-1 text-gray-500">
                {appliedCount} region{appliedCount === 1 ? "" : "s"} customized
              </span>
            )}
          </p>

          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { id: "wall", label: "Wall" },
              { id: "floor", label: "Floor" },
            ].map((surface) => (
              <button
                key={surface.id}
                type="button"
                onClick={() => setSurfaceFilter(surface.id)}
                className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition ${
                  surfaceFilter === surface.id
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="text-base">{surface.id === "wall" ? "✹" : "▦"}</span>
                {surface.label}
              </button>
            ))}
          </div>

          <div className="mb-3 grid grid-cols-3 gap-2">
            {productTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  activeTab === tab.id
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab !== "colors" && (
            <>
              <div className="grid grid-cols-[3rem_1fr_3rem_3rem] gap-2 mb-3">
                <label className="flex h-10 items-center justify-center rounded-lg border border-gray-200 bg-white">
                  <svg className="h-5 w-5 text-gray-800" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
                  </svg>
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="sr-only"
                    placeholder="Search products"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setShowFilters((value) => !value)}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M7 12h10M10 17h4" />
                  </svg>
                  Filters
                </button>
                <button
                  type="button"
                  onClick={() => setProductLayout("list")}
                  className={`flex h-10 items-center justify-center rounded-lg border ${
                    productLayout === "list" ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  ☰
                </button>
                <button
                  type="button"
                  onClick={() => setProductLayout("grid")}
                  className={`flex h-10 items-center justify-center rounded-lg border ${
                    productLayout === "grid" ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  ⊞
                </button>
              </div>

              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search products by name, size, material..."
                className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-900"
              />

              {showFilters && (
                <div className="mb-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <select
                    value={activeTab}
                    onChange={(event) => setActiveTab(event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm"
                  >
                    <option value="tiles">Tiles</option>
                    <option value="wallpapers">Wallpapers</option>
                    <option value="colors">Colors</option>
                  </select>
                  <select
                    value={selectedSize}
                    onChange={(event) => setSelectedSize(event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm"
                  >
                    <option value="">All sizes</option>
                    {(availableFilters?.sizes || []).map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <select
                    value={selectedMaterialFilter}
                    onChange={(event) => setSelectedMaterialFilter(event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm"
                  >
                    <option value="">All materials</option>
                    {(availableFilters?.materials || []).map((material) => (
                      <option key={material} value={material}>{material}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                <span>⇅</span>
                <select
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent outline-none"
                >
                  <option value="newest">New Products First</option>
                  <option value="price_asc">Price Low to High</option>
                  <option value="price_desc">Price High to Low</option>
                </select>
              </div>
            </>
          )}

          {renderContent()}
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white p-2">
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => {
                setSettings(DEFAULT_REGION_SETTINGS);
                if (onClearMaterials) {
                  onClearMaterials();
                }
              }}
              className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs text-gray-700 hover:bg-gray-100"
              title="Reset tile settings"
            >
              <span className="text-lg leading-none">↻</span>
              Reset
            </button>
            <button
              type="button"
              onClick={() => setShowGroutSettings((prev) => !prev)}
              className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs text-gray-700 hover:bg-gray-100"
              title="Grout settings"
            >
              <span className="text-lg leading-none">▦</span>
              Grout
            </button>
          </div>
          
          {showGroutSettings && (
            <div className="mt-2 space-y-3 rounded-lg bg-gray-50 p-3">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-600">Color</span>
                <input
                  type="color"
                  value={settings.groutColor}
                  onChange={(e) => updateSetting("groutColor", e.target.value)}
                  className="h-8 w-12 rounded border border-gray-200 cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Width</span>
                <input
                  type="range"
                  min="0"
                  max="0.02"
                  step="0.001"
                  value={settings.groutWidth}
                  onChange={(e) => updateSetting("groutWidth", parseFloat(e.target.value))}
                  className="min-w-0 flex-1 accent-gray-900"
                  title="Grout width"
                />
                <span className="w-12 text-right text-xs text-gray-600">
                  {(settings.groutWidth * 304.8).toFixed(1)} mm
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (onRemoveGrout) {
                    onRemoveGrout();
                  }
                }}
                className="w-full px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Remove Grout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Product Details Modal Component ── */}
      {detailProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setDetailProduct(null)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            {/* Image & Title */}
            <div className="flex gap-4">
              <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                {detailProduct.image && (
                  <img
                    src={detailProduct.image}
                    alt={detailProduct.name}
                    className="h-full w-full object-cover"
                  />
                )}
                <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/30 text-white text-xs">
                  ♥
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="break-all text-sm font-bold leading-tight text-gray-900">
                  {detailProduct.name}
                </h3>
              </div>
            </div>

            {/* Specifications Table */}
            <div className="mt-6">
              <h4 className="font-bold text-gray-900">Specifications</h4>
              <div className="mt-2 divide-y divide-gray-100 border-t border-b border-gray-100 text-xs text-gray-700">
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">SKU</span>
                  <span className="font-semibold text-gray-900">{detailProduct.sku || "-"}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Application Type</span>
                  <span className="font-semibold text-gray-900">
                    {detailProduct.application_type || "-"}
                  </span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Surface</span>
                  <span className="font-semibold text-gray-900">
                    {detailProduct.surface_type || "-"}
                  </span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Size</span>
                  <span className="font-semibold text-gray-900">
                    {formatSize(detailProduct)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  setDetailProduct(null);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-black py-2.5 text-xs font-medium text-white transition hover:bg-gray-800"
              >
                Add to cart 🛍️
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}